import { useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  EMPTY_FILTERS,
  FILTER_FIELD_LABELS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  type FilterFieldKey,
  type LloFunnelFiltersState,
  type SortableColumn,
  type SortDirection,
} from "./types";
import { countActiveFilters, hasActiveFilters } from "./filtering";

type FilterOptions = Record<FilterFieldKey, string[]>;

type LloFunnelFiltersProps = {
  filters: LloFunnelFiltersState;
  filterOptions: FilterOptions;
  orderBy: SortableColumn;
  order: SortDirection;
  loading: boolean;
  downloading: boolean;
  canDownload: boolean;
  onFiltersChange: (next: LloFunnelFiltersState) => void;
  onOrderByChange: (column: SortableColumn) => void;
  onOrderChange: (direction: SortDirection) => void;
  onLoad: () => void;
  onDownload: () => void;
  onClear: () => void;
};

const FILTER_FIELDS: FilterFieldKey[] = [
  "caseName",
  "raa",
  "ap",
  "lloExpiration",
  "negotiation",
  "funnel",
  "taskName",
  "taskAssignee",
];

function FilterAutocomplete({
  field,
  value,
  options,
  onChange,
}: {
  field: FilterFieldKey;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <Autocomplete
      size="small"
      options={options}
      value={value || null}
      onChange={(_, next) => onChange(next ?? "")}
      renderInput={(params) => (
        <TextField {...params} label={FILTER_FIELD_LABELS[field]} placeholder="All" />
      )}
      fullWidth
    />
  );
}

function buildActiveChips(filters: LloFunnelFiltersState) {
  const chips: { key: keyof LloFunnelFiltersState; label: string }[] = [];
  FILTER_FIELDS.forEach((field) => {
    if (filters[field]) {
      chips.push({
        key: field,
        label: `${FILTER_FIELD_LABELS[field]}: ${filters[field]}`,
      });
    }
  });
  if (filters.dueDateFrom) {
    chips.push({
      key: "dueDateFrom",
      label: `Due from: ${filters.dueDateFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.dueDateTo) {
    chips.push({
      key: "dueDateTo",
      label: `Due to: ${filters.dueDateTo.format("MM/DD/YYYY")}`,
    });
  }
  return chips;
}

export function LloFunnelFilters({
  filters,
  filterOptions,
  orderBy,
  order,
  loading,
  downloading,
  canDownload,
  onFiltersChange,
  onOrderByChange,
  onOrderChange,
  onLoad,
  onDownload,
  onClear,
}: LloFunnelFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const filtersActive = hasActiveFilters(filters);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);

  const patchFilters = (patch: Partial<LloFunnelFiltersState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  const clearField = (key: keyof LloFunnelFiltersState) => {
    if (key === "dueDateFrom" || key === "dueDateTo") {
      patchFilters({ [key]: null });
      return;
    }
    patchFilters({ [key]: "" });
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ px: 1.5, py: 1, flexShrink: 0, bgcolor: "grey.50" }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={onLoad}
              disabled={loading}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={onDownload}
              disabled={downloading || loading || !canDownload}
            >
              Download
            </Button>
            <Badge
              badgeContent={activeCount}
              color="primary"
              overlap="rectangular"
              sx={{ "& .MuiBadge-badge": { right: 6, top: 6 } }}
            >
              <Button
                variant={filtersActive ? "contained" : "outlined"}
                color={filtersActive ? "primary" : "inherit"}
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setDrawerOpen(true)}
                sx={filtersActive ? undefined : { bgcolor: "background.paper" }}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<FilterAltOffIcon />}
              onClick={onClear}
              disabled={loading || !filtersActive}
            >
              Clear filters
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <FormControl
              size="small"
              sx={{ minWidth: 170, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <InputLabel id="llo-sort-by-label">Sort by</InputLabel>
              <Select
                labelId="llo-sort-by-label"
                label="Sort by"
                value={orderBy}
                onChange={(e) => onOrderByChange(e.target.value as SortableColumn)}
              >
                {SORT_FIELD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 130, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <InputLabel id="llo-sort-dir-label">Direction</InputLabel>
              <Select
                labelId="llo-sort-dir-label"
                label="Direction"
                value={order}
                onChange={(e) => onOrderChange(e.target.value as SortDirection)}
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {activeChips.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {activeChips.map((chip) => (
              <Chip
                key={String(chip.key)}
                size="small"
                label={chip.label}
                onDelete={() => clearField(chip.key)}
                sx={{ bgcolor: "background.paper" }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 380 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Narrow results by any column
            </Typography>
          </Box>
          <IconButton aria-label="Close filters" onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
          <Stack spacing={1.75}>
            {FILTER_FIELDS.map((field) => (
              <FilterAutocomplete
                key={field}
                field={field}
                value={filters[field]}
                options={field === "negotiation" ? ["Yes", "No"] : filterOptions[field]}
                onChange={(value) => patchFilters({ [field]: value })}
              />
            ))}

            <DatePicker
              label="Due date from"
              value={filters.dueDateFrom}
              onChange={(value: Dayjs | null) => patchFilters({ dueDateFrom: value })}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="Due date to"
              value={filters.dueDateTo}
              onChange={(value: Dayjs | null) => patchFilters({ dueDateTo: value })}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Stack>
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <Button
            variant="outlined"
            color="warning"
            fullWidth
            startIcon={<FilterAltOffIcon />}
            onClick={onClear}
            disabled={!filtersActive}
          >
            Clear
          </Button>
          <Button variant="contained" fullWidth onClick={() => setDrawerOpen(false)}>
            Done
          </Button>
        </Stack>
      </Drawer>
    </>
  );
}
